import React, { useMemo } from 'react';
import { Gift } from 'lucide-react';
import { useSelector } from 'react-redux';
import { getCustomerSelector } from '@modules/customer';
import { getSubscriptionCycleSelector } from '@modules/subscriptions/core/selectors';
import { getRewardsForCycle, MOBILE_VISIBLE_ITEMS } from './utils/rewardsConfig';
import { TimelineItem } from './components/TimelineItem';
import clsx from 'clsx';
import { FormattedMessage, useIntl } from 'react-intl';

export const SubscriptionTimeline = () => {
  const intl = useIntl();
  const customer = useSelector(getCustomerSelector);
  // Read off the subscription's line SKU rather than the variant's product_info
  // metafield: the SKU is where the cycle actually lives, the selector already
  // parses every day count the selling plans expose (7/14/21/28/35/42/56), and it
  // falls back to 14 on its own when there is nothing to parse.
  const cycle = useSelector(getSubscriptionCycleSelector);

  const currentFoc = useMemo(() => {
    if (!customer?.tags) return 1;

    const tags = Array.isArray(customer.tags)
      ? customer.tags
      : typeof customer.tags === 'string'
        ? (customer.tags as string).split(',')
        : [];

    const focTag = tags.find((t: string) => t.trim().startsWith('FOC-'));
    if (!focTag) return 1;

    const match = focTag.trim().match(/FOC-(\d+)/);
    return match ? parseInt(match[1], 10) : 1;
  }, [customer]);

  const availableRewards = useMemo(() => getRewardsForCycle(cycle), [cycle]);

  const effectiveCurrentOrder = useMemo(() => {
    const hasReward = availableRewards.some((r) => r.order === currentFoc);
    if (hasReward) return currentFoc;

    const lastReward = availableRewards
      .filter((r) => r.order < currentFoc)
      .sort((a, b) => b.order - a.order)[0];

    return lastReward?.order || availableRewards[0]?.order || currentFoc;
  }, [currentFoc, availableRewards]);

  const isCurrentOrderSkipped = useMemo(
    () => !availableRewards.some((r) => r.order === currentFoc),
    [currentFoc, availableRewards],
  );

  const currentIndex = useMemo(
    () => availableRewards.findIndex((r) => r.order === effectiveCurrentOrder),
    [availableRewards, effectiveCurrentOrder],
  );

  // Only the narrow layout windows the schedule; `md` and up renders every stop.
  const mobileVisibleRewards = useMemo(() => {
    const itemsToShow = MOBILE_VISIBLE_ITEMS;
    const total = availableRewards.length;

    if (currentIndex === -1) return availableRewards.slice(0, itemsToShow);

    if (isCurrentOrderSkipped) {
      const start = currentIndex;
      const end = Math.min(total, start + itemsToShow);
      return availableRewards.slice(start, end);
    }

    if (currentIndex === 0) {
      return availableRewards.slice(0, Math.min(itemsToShow, total));
    }

    if (currentIndex === total - 1) {
      return availableRewards.slice(Math.max(0, total - itemsToShow));
    }

    const halfItems = Math.floor(itemsToShow / 2);
    const start = Math.max(0, currentIndex - halfItems);
    const end = Math.min(total, start + itemsToShow);

    return availableRewards.slice(start, end);
  }, [availableRewards, currentIndex, isCurrentOrderSkipped]);

  const progressWidths = useMemo(() => {
    const total = availableRewards.length;

    let desktopProgress = total <= 1 ? 0 : (Math.max(0, currentIndex) / (total - 1)) * 100;

    if (isCurrentOrderSkipped && currentIndex >= 0 && currentIndex < total - 1) {
      const nextRewardIndex = currentIndex + 1;
      const nextReward = availableRewards[nextRewardIndex];
      const currentReward = availableRewards[currentIndex];

      if (nextReward && currentReward) {
        // Calculate how far we are between the two rewards
        const gap = nextReward.order - currentReward.order;
        const progressInGap = currentFoc - currentReward.order;
        const fraction = progressInGap / gap;

        const baseProgress = (currentIndex / (total - 1)) * 100;
        const nextProgress = (nextRewardIndex / (total - 1)) * 100;

        desktopProgress = baseProgress + (nextProgress - baseProgress) * fraction;
      }
    }

    const currentIndexInVisible = mobileVisibleRewards.findIndex(
      (r) => r.order === effectiveCurrentOrder,
    );

    let mobileProgress = 0;
    if (currentIndexInVisible !== -1 && mobileVisibleRewards.length > 1) {
      const visibleTotal = mobileVisibleRewards.length;

      if (isCurrentOrderSkipped && currentIndexInVisible < visibleTotal - 1) {
        const nextRewardIndex = currentIndex + 1;
        const nextReward = availableRewards[nextRewardIndex];
        const currentReward = availableRewards[currentIndex];

        if (nextReward && currentReward) {
          const gap = nextReward.order - currentReward.order;
          const progressInGap = currentFoc - currentReward.order;
          const fraction = progressInGap / gap;

          const currentPos = (currentIndexInVisible / (visibleTotal - 1)) * 100;
          const nextPos = ((currentIndexInVisible + 1) / (visibleTotal - 1)) * 100;
          mobileProgress = currentPos + (nextPos - currentPos) * fraction;
        }
      } else {
        mobileProgress = (currentIndexInVisible / (mobileVisibleRewards.length - 1)) * 100;
      }
    } else if (mobileVisibleRewards.length === 1) {
      mobileProgress = 0;
    }

    return { desktop: desktopProgress, mobile: mobileProgress };
  }, [
    availableRewards,
    currentIndex,
    mobileVisibleRewards,
    effectiveCurrentOrder,
    isCurrentOrderSkipped,
    currentFoc,
  ]);

  const nextReward = useMemo(
    () => availableRewards.find((r) => r.order > currentFoc),
    [availableRewards, currentFoc],
  );

  return (
    <div className="subscription-timeline">
      <div className="widget-header">
        <h2>
          <FormattedMessage
            id="TIMELINE.HEADING"
            values={{ strong: (chunks) => <span className="font-bold">{chunks}</span> }}
          />
        </h2>
      </div>

      <div
        className={clsx('subscription-timeline__content', {
          'subscription-timeline__content--no-next-reward': !nextReward,
        })}>
        <p className="subscription-timeline__subtitle">
          <FormattedMessage id="TIMELINE.SUBTITLE" />
        </p>

        <div className="subscription-timeline__desktop">
          <div className="subscription-timeline__progress-line">
            <div
              className="subscription-timeline__progress-line-fill"
              style={{ width: `${progressWidths.desktop}%` }}
            />
          </div>

          <div className="subscription-timeline__timeline-items">
            {availableRewards.map((reward) => (
              <TimelineItem key={reward.order} reward={reward} currentFoc={currentFoc} />
            ))}
          </div>
        </div>

        <div className="subscription-timeline__mobile">
          <div className="subscription-timeline__mobile-progress-line">
            <div
              className="subscription-timeline__mobile-progress-line-fill"
              style={{ width: `${progressWidths.mobile}%` }}
            />
          </div>

          <div className="subscription-timeline__mobile-items">
            {mobileVisibleRewards.map((reward) => (
              <TimelineItem key={reward.order} reward={reward} currentFoc={currentFoc} />
            ))}
          </div>
        </div>
      </div>

      {/* No <Divider /> between the body and the footer: the two bands differ in
          colour, and `.divider` forces `background-color: #fff !important`, which
          put a 1px white seam above the footer. */}
      {nextReward && (
        <div className="subscription-timeline__next-reward">
          <div className="subscription-timeline__next-reward-icon">
            <Gift size={48} strokeWidth={2} />
          </div>
          <div className="subscription-timeline__next-reward-content">
            <span className="flex items-center gap-2">
              {' '}
              <p className="subscription-timeline__next-reward-title">
                <FormattedMessage
                  id="TIMELINE.NEXT_UP"
                  values={{
                    // `label` is a message id — see rewardsConfig.ts
                    label: intl.formatMessage({ id: nextReward.label }),
                    strong: (chunks) => <span className="font-bold">{chunks}</span>,
                  }}
                />
              </p>
            </span>
            <p className="subscription-timeline__next-reward-description">
              <FormattedMessage id="TIMELINE.NEXT_REWARD" />
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
